import { ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";

export function AboutContent() {
  return (
    <>
      {/* How Our Story Began */}
      <section className="surface-warm circuit-veil relative py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              <span className="label-rule text-muted-foreground">How Our Story Began</span>
              <h2 className="mt-5 font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
                From one sister helping another to a community of 34,000+
              </h2>
            </div>

            <div className="space-y-6 text-base leading-relaxed text-muted-foreground">
              <p>
                In 2023, our founder, Atoofah Gierdien, watched her then 18-year-old sister prepare for
                her matric exams. She studied until 3 a.m. during exam season, drowning in notes. She
                could not afford a tutor or study guides.
              </p>
              <p>
                Her sister tried matric apps, but the experience was fragmented. ChatGPT was not
                consistently aligned with CAPS. YouTube videos were too long for the time she had
                left, and TikTok did not have the short, focused lessons she needed.
              </p>
              <p>
                Atoofah began posting short CAPS-aligned lessons on TikTok to help her sister
                prepare.
              </p>
              <p>
                What started with one sister helping another became Crash Course Central and grew
                into a learning community of more than 34,000 people. Crash Course Central then
                evolved into adaptED, the next chapter of that story, moving beyond delivering
                content to building solutions that solve this problem.
              </p>
            </div>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-3">
            <div className="rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
              <p className="font-display text-4xl font-bold text-primary-strong">2023</p>
              <p className="mt-2 text-sm text-muted-foreground">Where it all began</p>
            </div>
            <div className="rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
              <p className="font-display text-4xl font-bold text-primary-strong">34K+</p>
              <p className="mt-2 text-sm text-muted-foreground">TikTok learning community</p>
            </div>
            <div className="rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
              <p className="font-display text-4xl font-bold text-primary-strong">adaptED</p>
              <p className="mt-2 text-sm text-muted-foreground">The next chapter</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Build For */}
      <section className="surface-ink relative py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="label-rule text-ink-foreground/75">Who We Build For</span>
            <h2 className="mt-5 font-display text-4xl leading-[1.05] text-ink-foreground sm:text-5xl">
              Built for learners, supported by the people around them
            </h2>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl bg-ink-foreground/10 p-8 backdrop-blur-sm">
              <h3 className="font-display text-2xl text-ink-foreground">Learners</h3>
              <p className="mt-4 text-sm leading-relaxed text-ink-foreground/80">
                South African high school learners, particularly matric learners who study
                independently, rely on their smartphones and cannot always access private tutoring
                or expensive study resources. Learners who deserve personalised support that meets
                them at their level without judgement.
              </p>
            </div>

            <div className="rounded-2xl bg-ink-foreground/10 p-8 backdrop-blur-sm">
              <h3 className="font-display text-2xl text-ink-foreground">Parents</h3>
              <p className="mt-4 text-sm leading-relaxed text-ink-foreground/80">
                Parents and guardians who want a clearer understanding of their child’s progress
                and how they can support their child at home.
              </p>
            </div>

            <div className="rounded-2xl bg-ink-foreground/10 p-8 backdrop-blur-sm">
              <h3 className="font-display text-2xl text-ink-foreground">Schools</h3>
              <p className="mt-4 text-sm leading-relaxed text-ink-foreground/80">
                Teachers and schools that need useful diagnostic insight to identify learning gaps
                and intervene earlier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission and Values */}
      <section className="surface-warm relative py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="label-rule text-muted-foreground">Our Mission and Values</span>
            <h2 className="mt-5 font-display text-4xl leading-[1.05] text-foreground sm:text-5xl">
              Accessible, intelligent support for every South African learner
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Our mission is to make intelligent, personalised academic support accessible to every
              South African learner, regardless of their school, income or starting point.
            </p>
          </div>

          <div className="mt-6 rounded-2xl border border-border/60 bg-card p-8 text-center shadow-[var(--shadow-card)] sm:p-10">
            <p className="font-display text-xl italic leading-relaxed text-foreground sm:text-2xl">
              “One product cannot solve every structural inequality in education. But we can give
              more learners affordable access to support that understands what they are learning,
              identifies where they are struggling and helps them move forward.” - Atoofah Gierdien
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <ValueCard
              title="Learner-first"
              description="We begin with what genuinely helps learners understand, improve and succeed."
            />
            <ValueCard
              title="Safe & private by design"
              description="We are building age-appropriate AI with parental consent for minors, responsible data practices and clear boundaries."
            />
            <ValueCard
              title="Accessible by default"
              description="Our learning experiences are designed to be affordable, mobile-first and easy to use."
            />
            <ValueCard
              title="Built for South Africa"
              description="Our technology is grounded in CAPS and the realities of South African learners, families and schools."
            />
            <ValueCard
              title="Better together"
              description="We build technology to strengthen the work of parents, teachers and schools, not replace it."
            />
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/join-the-pilot"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
            >
              Join the pilot
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function ValueCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="group rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card-hover)]">
      <h3 className="font-display text-xl text-card-foreground">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>
    </div>
  );
}
